import { useRef, useState } from 'react'
import {
  LINE_BY_ID, STATIONS, STATION_ADJACENCY,
  type StationId,
} from '../data/map'
import type { TMSnapshot } from '../data/types'
import { useT } from '../../../i18n'

interface Props {
  snapshot: TMSnapshot
  /** Station id currently interactive for this player (if any). */
  activeMeepleStationId?: StationId | null
  /** Stations tappable right now (e.g. during movement phase). */
  tappableStations?: Set<StationId>
  onTapStation?: (id: StationId) => void
}

/**
 * Tokyo Metro map view — bare print SVG with pan/zoom and very light
 * overlays (only what the printed map can't show: live meeple
 * positions, pyramid ownership, and tap targets during movement).
 *
 * - Pan: single-finger drag / mouse drag.
 * - Zoom: two-finger pinch (mobile), wheel (desktop).
 * - Tap targets: only rendered for `tappableStations` during the
 *   movement phase, sized small so the printed station name still
 *   reads through.
 */
export function TokyoMetroMapView({
  snapshot, activeMeepleStationId, tappableStations, onTapStation,
}: Props) {
  const t = useT()
  const { state, players } = snapshot

  // Group meeples + pyramid owners by station id for quick lookup.
  const meeplesByStation = new Map<StationId, typeof players>()
  for (const p of players) {
    const sid = p.state.meepleStation
    if (!sid) continue
    const arr = meeplesByStation.get(sid) ?? []
    arr.push(p)
    meeplesByStation.set(sid, arr)
  }
  const pyramidOwnerByStation = new Map<StationId, string>()
  for (const s of state.stations ?? []) {
    if (s.ownerId) pyramidOwnerByStation.set(s.stationId, s.ownerId)
  }
  const playerById = new Map(players.map(p => [p.id, p]))

  return (
    <div className="w-full">
      <PanZoom>
        <div className="relative">
          <img
            src="/games/tokyo-metro/Metro-Map.svg"
            alt={t('Tokyo Metro map')}
            className="w-full h-auto block select-none pointer-events-none"
            draggable={false}
          />

          {/* Overlay layer — small markers only. The printed SVG
              already shows station names + line colours, so we add
              just the live-state badges (meeples, pyramids, tap
              targets). */}
          <div className="absolute inset-0">
            {STATIONS.map(s => {
              const isTappable = tappableStations?.has(s.id) ?? false
              const meeples = meeplesByStation.get(s.id) ?? []
              const pyramidOwner = pyramidOwnerByStation.get(s.id)
              const pyramidPlayer = pyramidOwner ? playerById.get(pyramidOwner) : null
              const isActive = activeMeepleStationId === s.id

              // No tap target, no badge → nothing to render here.
              if (!isTappable && meeples.length === 0 && !pyramidPlayer && !isActive) {
                return null
              }

              return (
                <div
                  key={s.id}
                  style={{ left: `${s.mapX}%`, top: `${s.mapY}%` }}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                >
                  {/* Tap target — only rendered when tappable.
                      A small ring, no big white circle, so the
                      printed name underneath still reads. */}
                  {isTappable && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onTapStation?.(s.id) }}
                      className="absolute -inset-3 w-12 h-12 rounded-full
                                 bg-brand-primary/15 border-2 border-brand-primary
                                 animate-pulse-glow active:scale-90 transition-all"
                      aria-label={t('Move to {name}', { name: s.name })}
                      title={s.name}
                    />
                  )}

                  {/* "I'm here" outer ring for the player's own meeple. */}
                  {isActive && (
                    <span className="absolute -inset-2 w-10 h-10 rounded-full
                                     ring-2 ring-brand-primary/70" />
                  )}

                  {/* Pyramid badge (top-right). */}
                  {pyramidPlayer && (
                    <span className="absolute -top-3 -right-2 text-[10px] font-bold text-white
                                     bg-brand-primary px-1 rounded leading-none py-0.5
                                     shadow-xs pointer-events-none"
                          title={t('Owned by {name}', { name: pyramidPlayer.display_name })}>
                      ▲
                    </span>
                  )}

                  {/* Meeple stack (bottom-centre). */}
                  {meeples.length > 0 && (
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2
                                    flex -space-x-1 pointer-events-none">
                      {meeples.slice(0, 4).map(p => (
                        <span key={p.id}
                              className="w-3 h-3 rounded-full border border-white shadow-sm"
                              style={{ backgroundColor: meepleColor(p.join_order) }}
                              title={p.display_name} />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </PanZoom>
      <LineLegend />
    </div>
  )
}

const MEEPLE_COLORS = ['#D4603A', '#4CAF50', '#1976D2', '#9C27B0', '#FF9800']
function meepleColor(idx: number): string {
  return MEEPLE_COLORS[idx % MEEPLE_COLORS.length]
}

/** Returns neighbors within N hops along the simplified grid. */
export function reachableStations(start: StationId, steps: number): Set<StationId> {
  const visited = new Set<StationId>([start])
  let frontier: StationId[] = [start]
  for (let i = 0; i < steps; i++) {
    const next: StationId[] = []
    for (const s of frontier) {
      for (const n of STATION_ADJACENCY[s] ?? []) {
        if (!visited.has(n)) { visited.add(n); next.push(n) }
      }
    }
    frontier = next
  }
  visited.delete(start)
  return visited
}

/**
 * Minimal pan/zoom wrapper. Single-finger drag pans (only after the
 * pointer has moved past a small threshold — so a quick tap on a
 * station button passes through to the button instead of being
 * swallowed). Two-finger pinch zooms; mouse drag pans, wheel zooms.
 * Stays in the [1,4] scale range — at scale 1 the bare map fits the
 * viewport.
 */
function PanZoom({ children }: { children: React.ReactNode }) {
  const t = useT()
  const [tr, setTr] = useState({ x: 0, y: 0, scale: 1 })
  // Per-pointer state. `panning` flips true once the pointer has
  // moved past PAN_THRESHOLD; before that, we treat the gesture as a
  // potential tap and DO NOT capture pointer / DO NOT pan.
  const pointersRef = useRef<Map<number, {
    x: number; y: number; startX: number; startY: number; panning: boolean
  }>>(new Map())
  const lastPanRef = useRef({ x: 0, y: 0 })
  const lastPinchRef = useRef<number | null>(null)
  const PAN_THRESHOLD = 6  // px

  function clampScale(s: number) { return Math.min(4, Math.max(1, s)) }

  function onPointerDown(e: React.PointerEvent) {
    pointersRef.current.set(e.pointerId, {
      x: e.clientX, y: e.clientY,
      startX: e.clientX, startY: e.clientY,
      panning: false,
    })
    if (pointersRef.current.size === 2) {
      const [a, b] = Array.from(pointersRef.current.values())
      lastPinchRef.current = Math.hypot(a.x - b.x, a.y - b.y)
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    const p = pointersRef.current.get(e.pointerId)
    if (!p) return
    p.x = e.clientX; p.y = e.clientY

    if (pointersRef.current.size === 1) {
      // Single pointer — gate panning behind a movement threshold so
      // taps still register as clicks on station buttons.
      if (!p.panning) {
        const dist = Math.hypot(e.clientX - p.startX, e.clientY - p.startY)
        if (dist < PAN_THRESHOLD) return
        p.panning = true
        lastPanRef.current = { x: e.clientX, y: e.clientY }
        // Now we know it's a pan, capture so we don't lose it if the
        // pointer leaves the wrapper.
        try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* noop */ }
      }
      const dx = e.clientX - lastPanRef.current.x
      const dy = e.clientY - lastPanRef.current.y
      lastPanRef.current = { x: e.clientX, y: e.clientY }
      setTr(t => ({ ...t, x: t.x + dx, y: t.y + dy }))
    } else if (pointersRef.current.size === 2) {
      const [a, b] = Array.from(pointersRef.current.values())
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      if (lastPinchRef.current && lastPinchRef.current > 0) {
        const ratio = dist / lastPinchRef.current
        setTr(t => ({ ...t, scale: clampScale(t.scale * ratio) }))
      }
      lastPinchRef.current = dist
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    const p = pointersRef.current.get(e.pointerId)
    if (p?.panning) {
      try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
    }
    pointersRef.current.delete(e.pointerId)
    if (pointersRef.current.size < 2) lastPinchRef.current = null
  }

  function onWheel(e: React.WheelEvent) {
    e.preventDefault()
    const ratio = e.deltaY > 0 ? 0.92 : 1.08
    setTr(t => ({ ...t, scale: clampScale(t.scale * ratio) }))
  }

  function reset() { setTr({ x: 0, y: 0, scale: 1 }) }

  return (
    <div className="relative rounded-2xl overflow-hidden bg-white border border-brand-border shadow-xs">
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        className="cursor-grab active:cursor-grabbing"
        // pan-y so vertical page-scroll still works when the user
        // isn't dragging the map; pinch-zoom we handle ourselves.
        style={{ touchAction: 'pan-y' }}
      >
        <div
          style={{
            transform: `translate(${tr.x}px, ${tr.y}px) scale(${tr.scale})`,
            transformOrigin: '0 0',
          }}
        >
          {children}
        </div>
      </div>
      {(tr.scale !== 1 || tr.x !== 0 || tr.y !== 0) && (
        <button
          onClick={reset}
          className="absolute top-2 right-2 px-3 py-1.5 rounded-full text-xs font-semibold
                     bg-brand-text text-white shadow active:scale-95"
          aria-label={t('Reset map view')}
        >{t('Reset view')}</button>
      )}
    </div>
  )
}

function LineLegend() {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5 px-1">
      {Object.values(LINE_BY_ID).map(l => (
        <div key={l.id} className="flex items-center gap-1 text-[9px] text-brand-muted">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color }} />
          <span className="font-display font-bold">{l.id}</span>
          <span className="lowercase">{l.name}</span>
        </div>
      ))}
    </div>
  )
}
